import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { logger } from 'common/services';
import { isFeatureEnabled } from 'common/utils';
import { generateDynamicPath } from 'common/utils/routing';
import { TranscriptionMode } from 'domain/event';
import { mapEventRoomFromApi, mapToEventRoomAttendeeFromApi } from 'event/mappers';
import { uniqBy } from 'lodash-es';
import { addNatteringInfoMessage, removeAppMessage } from 'modules/alerts/state';
import { NattersRoutes } from 'natters/routes';
import { push } from 'redux-first-history';
import type { RootState } from 'store';
import { selectUserAccountId } from 'user/api';

import { addSupportRequestedMessage } from '../actions';
import {
  guestJoinedRoom_WS,
  joinRoom_WS,
  leaveRoomAsSupport,
  meetingBroadcastCountdownStarted_WS,
  meetingRoomClosed_WS,
  natterEnded_WS,
  natteringActions,
  roomSupportCancelled_WS,
  roomSupportJoined_WS,
  roomSupportLeft_WS,
  roomSupportRequestedList_WS,
  roomSupportRequested_WS,
  startTranscription,
  stopTranscription,
} from '../actions/room.actions';
import {
  natteringAcceptedEffect,
  natteringAudioIssueDetectedBeforeJoining,
  natteringAutomaticallySkippedEffect,
  natteringSkippedEffect,
} from '../effects/room.effects';
import {
  addLastCallQuest,
  addRoom,
  hideTranscriptionModal,
  removeRoom,
  setDisconnectedParticipants,
  updateRoom,
} from '../eventSlice.actions';
import { selectIsCurrentUserEventHost, selectIsUserEventHost, selectRoomsRequestingSupport } from '../selectors';
import { findRoomById, getActiveRoom } from '../utils';
import { meetingListener } from './meeting.listener';

export const roomListener = createListenerMiddleware<RootState>();

roomListener.startListening({
  actionCreator: natteringActions.joinNatteringModal.accepted,
  effect: natteringAcceptedEffect,
});

roomListener.startListening({
  matcher: isAnyOf(
    natteringActions.joinNatteringModal.skipped,
    natteringActions.joinNatteringModal.audioIssueDetected,
    natteringActions.joinNatteringModal.automaticallySkippedDueToNetworkConditions
  ),
  effect: natteringSkippedEffect,
});

roomListener.startListening({
  actionCreator: natteringActions.joinNatteringModal.automaticallySkippedDueToNetworkConditions,
  effect: natteringAutomaticallySkippedEffect,
});

roomListener.startListening({
  actionCreator: natteringActions.joinNatteringModal.audioIssueDetected,
  effect: natteringAudioIssueDetectedBeforeJoining,
});

roomListener.startListening({
  actionCreator: meetingRoomClosed_WS,
  effect: ({ payload }, { getState, dispatch }) => {
    const state = getState();

    const room = getActiveRoom(state.event);
    if (!room) return;

    const isCurrentUserHost = selectIsCurrentUserEventHost(state);

    if (payload.id === room.id && isCurrentUserHost) {
      // Event host should leave room immediately
      dispatch(leaveRoomAsSupport({ roomId: room.id }));
    }
  },
});

roomListener.startListening({
  actionCreator: guestJoinedRoom_WS,
  effect: ({ payload }, { getState, dispatch }) => {
    logger.info('[roomListener] Guest joined room', { guest: { ...payload } });

    const state = getState();
    const existingRoom = findRoomById(state.event.rooms, payload.roomId);
    const isCurrentUserHost = selectIsCurrentUserEventHost(state);
    const shouldAddNewRoom = !existingRoom && !(state.event.isBigMeetingMode && isCurrentUserHost);
    const newAttendee = mapToEventRoomAttendeeFromApi(payload);

    if (shouldAddNewRoom) {
      dispatch(
        addRoom({
          room: {
            attendees: [newAttendee],
            id: payload.roomId,
            accessToken: '',
            startTime: 0,
            endTime: 0,
            orderNumber: 0,
            roomQuestionAssignments: [],
          },
        })
      );
    }

    if (existingRoom) {
      dispatch(
        updateRoom({
          room: {
            ...existingRoom,
            attendees: uniqBy([newAttendee, ...existingRoom.attendees], 'id'),
          },
        })
      );
    }

    dispatch(setDisconnectedParticipants(state.event.participantsDisconnected.filter((id) => id !== payload.userId)));

    if (!state.event.activeEventId) return;

    if (!isCurrentUserHost) {
      const attendee = mapToEventRoomAttendeeFromApi(payload);
      const isUserHost = selectIsUserEventHost(state, attendee.id);
      if (!isUserHost) {
        dispatch(addLastCallQuest(attendee));
      }
    }
  },
});

roomListener.startListening({
  actionCreator: meetingRoomClosed_WS,
  effect: ({ payload }, { getState, dispatch }) => {
    const { event } = getState();
    if (event.activeRoom?.id === payload.id && event.activeRoom.joinedAsSupport) {
      dispatch(leaveRoomAsSupport({ roomId: event.activeRoom.id }));
    }
  },
});

roomListener.startListening({
  actionCreator: roomSupportRequested_WS,
  effect: ({ payload }, { dispatch, getState }) => {
    const state = getState();
    const isCurrentUserHost = selectIsCurrentUserEventHost(state);

    if (isCurrentUserHost && state.event.isBigMeetingMode) {
      const room = mapEventRoomFromApi(payload.room);
      dispatch(addRoom({ room }));
      return;
    }

    if (isCurrentUserHost) {
      if (isFeatureEnabled('BackstageV2')) {
        dispatch(addSupportRequestedMessage);
      }
    } else {
      logger.info('[roomListener] Support requested', { ...payload });
      dispatch(
        addNatteringInfoMessage('Natter Support has been notified. If they are available, they will join shortly')
      );
    }
  },
});

roomListener.startListening({
  actionCreator: roomSupportRequestedList_WS,
  effect: ({ payload }, { dispatch, getState }) => {
    const state = getState();

    const roomsRequestingSupport = selectRoomsRequestingSupport(state);
    if (isFeatureEnabled('BackstageV2') && roomsRequestingSupport.length) {
      dispatch(addSupportRequestedMessage);
    }

    if (!state.event.isBigMeetingMode) return;

    const isCurrentUserHost = selectIsCurrentUserEventHost(state);
    if (!isCurrentUserHost) return;

    payload.rooms.forEach((roomData) => {
      const room = mapEventRoomFromApi(roomData);
      dispatch(addRoom({ room }));
    });
  },
});

roomListener.startListening({
  actionCreator: roomSupportJoined_WS,
  effect: ({ payload }, { dispatch, getState }) => {
    const state = getState();
    const isCurrentUserHost = selectIsCurrentUserEventHost(state);
    const room = findRoomById(state.event.rooms, payload.roomId);

    if (isCurrentUserHost && room && !room.accessToken) {
      dispatch(
        updateRoom({
          room: {
            ...room,
            accessToken: payload.user.agoraToken,
          },
        })
      );
    }
  },
});

roomListener.startListening({
  actionCreator: roomSupportCancelled_WS,
  effect: ({ payload }, { dispatch, getState }) => {
    const state = getState();

    const roomsRequestingSupport = selectRoomsRequestingSupport(state);
    if (!roomsRequestingSupport.length) {
      dispatch(removeAppMessage(addSupportRequestedMessage.payload.id));
    }

    if (!state.event.isBigMeetingMode) return;

    const isUserHost = selectIsCurrentUserEventHost(state);
    const isInRoom = state.event.activeRoom?.id === payload.roomId;
    if (isUserHost && !isInRoom) {
      dispatch(removeRoom({ roomId: payload.roomId }));
    }
  },
});

roomListener.startListening({
  actionCreator: roomSupportLeft_WS,
  effect: ({ payload }, { dispatch, getState }) => {
    const { event } = getState();
    const attendee = mapToEventRoomAttendeeFromApi(payload.user);
    const room = findRoomById(event.rooms, payload.roomId);
    if (room) {
      dispatch(
        updateRoom({
          room: {
            ...room,
            attendees: room.attendees.filter((a) => a.id !== attendee.id),
          },
        })
      );
    }
  },
});

roomListener.startListening({
  actionCreator: leaveRoomAsSupport.fulfilled,
  effect: (
    {
      meta: {
        arg: { roomId },
      },
    },
    { dispatch, getState }
  ) => {
    const state = getState();
    const accountId = selectUserAccountId(state);

    if (accountId) {
      const room = findRoomById(state.event.rooms, roomId);

      if (room) {
        dispatch(
          updateRoom({
            room: {
              ...room,
              attendees: room.attendees.filter((a) => a.id !== accountId),
            },
          })
        );
      }
    }
  },
});

meetingListener.startListening({
  actionCreator: joinRoom_WS,
  effect: ({ payload: { hasSetTranscriptionPreference, transcriptionMode, id } }, { dispatch }) => {
    if (transcriptionMode === TranscriptionMode.MANDATORY && !hasSetTranscriptionPreference) {
      // this may seem redundant but it's required in v1
      dispatch(startTranscription({ roomId: id }));
    }
  },
});

meetingListener.startListening({
  matcher: isAnyOf(startTranscription.fulfilled, stopTranscription.fulfilled),
  effect: (_, { dispatch }) => {
    dispatch(hideTranscriptionModal());
  },
});

meetingListener.startListening({
  actionCreator: natterEnded_WS,
  effect: ({ payload }, { dispatch }) => {
    dispatch(push(generateDynamicPath(NattersRoutes.NatterEnded, { id: payload }), { allow: true }));
  },
});

roomListener.startListening({
  actionCreator: meetingBroadcastCountdownStarted_WS,
  effect: (_, { dispatch }) => {
    dispatch(addNatteringInfoMessage('The Broadcast will resume soon'));
  },
});
